import { Text, useMantineTheme } from '@mantine/core';
import { Port, PortDirection } from '@morph-mapper/types';
import { Handle as RFHandle, Position, HandleType } from '@xyflow/react';
import { match } from 'ts-pattern';
import { BsPlus } from 'react-icons/bs';
import { useState } from 'react';
import { HANDLE_SPACING } from './config';

export interface HandleProps {
  index: number;
  total: number;
  port: Port;
  showAdd?: boolean;
  onAdd?: () => void;
}

export const Handle = ({
  index,
  total,
  port,
  showAdd = false,
  onAdd,
}: HandleProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const theme = useMantineTheme();

  const offset = (index - (total - 1) / 2) * HANDLE_SPACING;

  const getType = () =>
    match(port.direction)
      .with(PortDirection.Input, () => 'target')
      .with(PortDirection.Output, () => 'source')
      .exhaustive();

  const getPosition = () =>
    match(port.direction)
      .with(PortDirection.Input, () => Position.Left)
      .with(PortDirection.Output, () => Position.Right)
      .exhaustive();

  const isInput = port.direction === PortDirection.Input;
  const isLabeled = Boolean(port.label);
  const showPlus = showAdd && (isHovered || port.multi || !isLabeled);
  const usePill = isLabeled || showAdd;

  const handleStyle = usePill
    ? {
        top: `calc(50% + ${offset}px)`,
        background: theme.white,
        border: `1px solid ${theme.colors.gray[2]}`,
        borderRadius: theme.radius.md,
        padding: '2px 6px',
        height: 'auto',
        width: 'auto',
        color: theme.colors.gray[7],
        fontFamily: theme.fontFamilyMonospace,
        fontSize: theme.fontSizes.xs,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: showAdd ? 'pointer' : 'default',
        ...(isInput
          ? { left: -12, transform: 'translate(-100%, -50%)' }
          : { right: -12, transform: 'translate(100%, -50%)' }),
      }
    : {
        top: `calc(50% + ${offset}px)`,
        background: theme.colors.gray[4],
        width: 8,
        height: 8,
        ...(isInput
          ? { left: -18, transform: 'translateY(-50%)' }
          : { right: -18, transform: 'translateY(-50%)' }),
      };

  return (
    <RFHandle
      key={`${port.direction}-${port.id}`}
      id={port.id}
      type={getType() as HandleType}
      position={getPosition()}
      style={handleStyle}
      className={showAdd ? 'port-handle--add' : undefined}
      onClick={(event) => {
        if (!showAdd) return;
        event.stopPropagation();
        onAdd?.();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {usePill && (
        <Text
          fz="xs"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {isLabeled ? (
            <>
              {port.label}
              {showPlus && <BsPlus size={14} />}
            </>
          ) : showPlus ? (
            <BsPlus size={14} />
          ) : null}
        </Text>
      )}
    </RFHandle>
  );
};
